/**
 * THE CANONICAL MIQ NUMBER FORMATTER AND ITS EXACT INVERSE.
 *
 * Basis: recovered from the lost `src/app/shared/utils/helpers/locale-number-format.ts`
 * (`formatLocaleNumberValue`, commit 2aca4b1ddf "feat: (CM-31519)-number-avail-calc" — the file is
 * absent from HEAD with no deleting commit). Its shape is kept — resolve locale, delegate to a CLDR
 * formatter, then rewrite the separators the partition demands — with four corrections:
 *   1. it had a positional `locale` option (see WHY THERE IS NO POSITIONAL LOCALE below);
 *   2. its dot-comma fix-up (`replace(/[\\s\\u00A0\\u202F]/g, '.')`) only handled space-family
 *      grouping, so de-CH / it-CH (CLDR groups with U+2019) came out unchanged;
 *   3. its space-comma fix-up only normalised U+00A0/U+202F, so pt-PT — which Angular resolves to
 *      `pt`, a DOT-grouping locale — came out as `1.234,56` instead of `1 234,56`;
 *   4. it had no null / masked / unparsable handling, and would throw NG0701 for Korean.
 *
 * ============================================================================================
 * WHY THERE IS NO POSITIONAL `locale` PARAMETER — DO NOT ADD ONE
 * ============================================================================================
 * 18 call sites in the auto-calculation WRITE-BACK path format with the literal `'en'` and then
 * re-parse the result to get the number they persist:
 *
 *   src/app/records/controls/auto-calculation/auto-calc-round.ts:21                    (1)
 *   src/app/records/controls/common/listeners/sale/sale-auto-calc-listeners.ts          (9)
 *   src/app/records/controls/common/listeners/common/common-listener-helper.ts          (4)
 *   src/app/business-rules/common/common-rate.ts                                       (4)
 *
 * They work ONLY because the existing `formatNumber` (`./sanitizers.ts` `MiqFormatNumber`) IGNORES
 * its `locale` argument and formats for the CURRENT USER LOCALE, while the matching
 * `parseFormattedNumber` branches on `getCurrentLocale()`. If `'en'` were honoured, a Polish user's
 * `1,234.56` would be handed to `parseCECommaDotFormattedValue`, which strips dots and finds no
 * comma — yielding `123456`, a 100x corruption written straight to EDP.
 *
 * So the locale is ALWAYS the user's. `opts.localeOverride` exists for the genuinely
 * locale-invariant cases (a fixed-format API payload, a snapshot compared byte-for-byte across
 * users) and is the only way to select a different one.
 */
import { formatNumber as ngFormatNumber, getLocaleNumberSymbol, NumberSymbol } from '@angular/common';
import {
  getLocaleNumberStyle,
  getMiqNumberSeparators,
  isCELocaleCode,
  isGroupingChar,
  isSpaceVariant,
} from './ce-locales.const';
import type { MiqNumberSeparators, MiqNumberStyle } from './ce-locales.const';
import { getCurrentLocale } from './locale-utils';
// `isMasked` is imported rather than re-implemented so there is ONE masking token in the library.
// The edge is one-way (sanitizers -> locale-utils -> ce-locales.const); if a later phase repoints
// `MiqFormatNumber` at `formatMiqNumber` it must move `isMasked` down, NOT import this module from
// `sanitizers.ts`, or the two files become a cycle.
import { isMasked } from './sanitizers';
import { defaultTxt } from './strings.constants';

export {
  getLocaleNumberStyle,
  getMiqNumberSeparators,
  isCELocaleCode,
  isGroupingChar,
  isSpaceVariant,
} from './ce-locales.const';
export type { MiqNumberSeparators, MiqNumberStyle } from './ce-locales.const';

/** The locale used whenever the resolved one has no registered Angular locale data. */
const FALLBACK_LOCALE = 'en-US';

/**
 * Default digits info, matching `MiqFormatNumber`'s so `formatMiqNumber` is a drop-in for it.
 * (`{minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}`.)
 */
const DEFAULT_DIGITS_INFO = '.0-2';

/**
 * `true` when the CURRENT USER LOCALE (or `locale`, if given) uses a COMMA as its decimal separator,
 * i.e. it is one of the two CE groups.
 *
 * Replaces the `CE_LOCALES.includes(getCurrentLocale())` idiom that was inlined at 8 call sites.
 * Callers that need to know WHICH separators to use want {@link getCurrentLocaleNumberStyle} or
 * {@link getCurrentLocaleSeparators} instead — the two CE groups do not agree with each other.
 */
export function isCELocale(locale: string = getCurrentLocale()): boolean {
  return isCELocaleCode(locale);
}

/** Zero-argument form of {@link getLocaleNumberStyle}, defaulting to the current user locale. */
export function getCurrentLocaleNumberStyle(locale: string = getCurrentLocale()): MiqNumberStyle {
  return getLocaleNumberStyle(locale);
}

/** Zero-argument form of {@link getMiqNumberSeparators}, defaulting to the current user locale. */
export function getCurrentLocaleSeparators(
  plainSpace = false,
  locale: string = getCurrentLocale()
): MiqNumberSeparators {
  return getMiqNumberSeparators(locale, plainSpace);
}

export interface MiqNumberFormatOptions {
  /**
   * Format for THIS locale instead of the user's. Use only where the output must be
   * locale-invariant (a fixed-format API payload, a byte-compared snapshot). Passing a locale here
   * for a value a user will READ is a bug — see the header note on the 18 auto-calc sites.
   */
  localeOverride?: string;
  /** Text returned when `value` is `null` / `undefined` / blank. Defaults to `'-'`. */
  defaultTxt?: string;
  /**
   * Emit U+0020 rather than U+00A0 as the thousands separator. For EXPORT paths only (a no-break
   * space can survive into a spreadsheet cell and stop it parsing as a number). Display paths must
   * leave this off so the grouped number cannot line-wrap.
   */
  plainSpace?: boolean;
}

export interface MiqNumberParseOptions {
  /** Parse as THIS locale instead of the user's. Mirrors {@link MiqNumberFormatOptions.localeOverride}. */
  localeOverride?: string;
  /** Returned for `null` / blank / masked / unparsable input. Defaults to `null`. */
  fallback?: number | null;
}

/**
 * Formats `value` for the CURRENT USER LOCALE per the MIQ partition (see `./ce-locales.const`).
 *
 * NULL / MASKED / UNPARSABLE SEMANTICS, preserved from `MiqFormatNumber` except where noted:
 *   - `null` / `undefined`      -> `opts.defaultTxt` (default `'-'`)
 *   - masked (`'*****'`)        -> returned verbatim
 *   - non-null, not a number    -> returned verbatim (`String(value)`)
 *   - `''` / whitespace-only    -> `opts.defaultTxt`  ** DELIBERATE DIVERGENCE, see below **
 *
 * THE `''` DECISION — FIXED, NOT PRESERVED. `MiqFormatNumber` renders `''` as `'0'`, because
 * `Number('') === 0` makes it look parsable. That is a display lie: a blank field is "no value", and
 * showing `0` makes users believe a real zero was entered (and, in the availability/rates screens,
 * that a rate of zero was agreed). `formatMiqNumber` is new code with no legacy callers, so the bug
 * is not carried forward. `MiqFormatNumber` itself is NOT changed here — ~190 display call sites
 * depend on its exact output and are repointed in a later phase; expect a visible `0` -> `-` diff
 * at those sites when they move.
 *
 * NEVER THROWS. Angular's `formatNumber` raises NG0701 for a locale with no registered locale data,
 * which today breaks EVERY number on the page for Korean users (`LocaleId` 53/54 -> `ko-KR`, and
 * `ko` was not registered). Registering `ko` fixes the root cause; this guard means a future
 * unregistered locale degrades to `en-US` grouping instead of blanking the screen.
 *
 * @param value        the number (or numeric string) to format
 * @param digitsInfo   Angular digits info, default `'.0-2'`
 */
export function formatMiqNumber(
  value: unknown,
  digitsInfo: string = DEFAULT_DIGITS_INFO,
  opts: MiqNumberFormatOptions = {}
): string {
  const fallbackText = opts.defaultTxt ?? defaultTxt;
  if (value == null) {
    return fallbackText;
  }
  if (isMasked(value)) {
    return value as string;
  }
  const raw = typeof value === 'string' ? value.trim() : value;
  if (raw === '') {
    return fallbackText;
  }
  const num = Number(raw);
  if (!Number.isFinite(num)) {
    // Not a number (and not masked): pass through verbatim, as MiqFormatNumber does. Note this also
    // catches Infinity/NaN, which Angular would render as '∞'/'NaN'.
    return String(value);
  }

  const locale = opts.localeOverride ?? getCurrentLocale();
  const target = getMiqNumberSeparators(locale, opts.plainSpace);
  const { text: cldrText, separators: cldr } = formatWithCldr(num, locale, digitsInfo);
  return rewriteSeparators(cldrText, cldr, target);
}

/**
 * Parses a MIQ-formatted number string back to a `number`. The exact inverse of
 * {@link formatMiqNumber} for the same locale.
 *
 * DELIBERATELY TOLERANT OF THE OTHER CE GROUP SEPARATOR. Under a CE locale, a comma is ALWAYS the
 * decimal separator, so both `1 234,56` and `1.234,56` parse to `1234.56`. This is not laxity — it
 * is required, because the legacy `MiqFormatNumber` emits a DOT group for `pt-PT` (Angular resolves
 * it to `pt`) while `formatMiqNumber` emits a SPACE, and both strings reach this function.
 *
 * ALSO TOLERANT OF RAW MACHINE STRINGS. Under a CE locale, a dot-only string whose dots are NOT a
 * valid 3-digit grouping pattern (`'1234.56'`, `'1.5'`) is read as a decimal point, not as
 * grouping. Without this, EDP values that arrive as plain numeric strings — e.g. everything
 * `@miq/stacking-plan`'s `formatNumbers` receives from the export resolvers — would be inflated
 * 100x for Polish and German users. `'1.234'` remains grouping (`1234`), matching the legacy CE
 * parsers, because that is what the formatters emit.
 *
 * @returns the number, or `opts.fallback` (default `null`) for null / blank / masked / unparsable
 */
export function parseMiqNumber(text: unknown, opts: MiqNumberParseOptions = {}): number | null {
  const fallback = opts.fallback ?? null;
  if (text == null) {
    return fallback;
  }
  if (typeof text === 'number') {
    return Number.isFinite(text) ? text : fallback;
  }
  const raw = String(text).trim();
  if (raw === '' || isMasked(raw)) {
    return fallback;
  }

  // U+2212 MINUS SIGN is what some CLDR locales emit for negatives, written as an escape.
  const negative = /^[-\u2212]/.test(raw);
  // Space-family characters are grouping separators in every locale MIQ reaches and are never
  // decimal separators, so they can be dropped before anything else.
  const despaced = raw.replace(/[\u0020\u00A0\u202F]/g, '');
  // Keep digits and the two ambiguous separators; drop currency symbols, unit suffixes, U+2019, etc.
  const digitsAndSeparators = despaced.replace(/[^0-9.,]/g, '');
  if (!/[0-9]/.test(digitsAndSeparators)) {
    return fallback;
  }

  const locale = opts.localeOverride ?? getCurrentLocale();
  const normalized =
    getLocaleNumberStyle(locale) === 'comma-dot'
      ? normalizeCommaDot(digitsAndSeparators)
      : normalizeCeComma(digitsAndSeparators);

  const num = Number(normalized);
  if (!Number.isFinite(num)) {
    return fallback;
  }
  return negative ? -num : num;
}

/**
 * `comma-dot` (en-*, zh-*, ko-*): the decimal separator is a DOT, full stop. Commas are grouping and
 * are always dropped — identical to the `replace(/,/g, '')` the legacy US-only parse sites used, so
 * there is no behaviour change for the majority of users. Only the multi-dot case is hardened: the
 * LAST dot wins and earlier ones are treated as grouping.
 */
function normalizeCommaDot(s: string): string {
  const withoutGroups = s.replace(/,/g, '');
  const lastDot = withoutGroups.lastIndexOf('.');
  if (lastDot < 0) {
    return withoutGroups;
  }
  return withoutGroups.slice(0, lastDot).replace(/\./g, '') + '.' + withoutGroups.slice(lastDot + 1);
}

/** A dot-grouped integer and nothing else: `1.234`, `1.234.567`. */
const PURE_DOT_GROUPING = /^[0-9]{1,3}(\.[0-9]{3})+$/;

/**
 * CE (`space-comma`, `dot-comma`): the decimal separator is a COMMA.
 *  - any comma present -> the LAST comma is the decimal point; every other comma and every dot is
 *    grouping.
 *  - no comma -> dots only. A valid grouping pattern is grouping; anything else is a decimal point
 *    (see the "raw machine strings" note on `parseMiqNumber`).
 */
function normalizeCeComma(s: string): string {
  const lastComma = s.lastIndexOf(',');
  if (lastComma >= 0) {
    const intPart = s.slice(0, lastComma).replace(/[.,]/g, '');
    const fracPart = s.slice(lastComma + 1).replace(/[.,]/g, '');
    return fracPart === '' ? intPart : `${intPart}.${fracPart}`;
  }
  if (!s.includes('.') || PURE_DOT_GROUPING.test(s)) {
    return s.replace(/\./g, '');
  }
  const lastDot = s.lastIndexOf('.');
  return s.slice(0, lastDot).replace(/\./g, '') + '.' + s.slice(lastDot + 1);
}

/**
 * Runs Angular's CLDR formatter and reports which separators it actually used, so
 * {@link rewriteSeparators} can swap them without guessing. Reading the symbols (rather than
 * sniffing the output) is what makes the override layer correct for a zero-decimal `digitsInfo`,
 * where the output contains no decimal separator at all.
 */
function formatWithCldr(
  num: number,
  locale: string,
  digitsInfo: string
): { text: string; separators: MiqNumberSeparators } {
  try {
    return {
      text: ngFormatNumber(num, locale, digitsInfo),
      separators: {
        group: getLocaleNumberSymbol(locale, NumberSymbol.Group),
        decimal: getLocaleNumberSymbol(locale, NumberSymbol.Decimal),
      },
    };
  } catch {
    // NG0701 "Missing locale data": fall back to en-US rather than let the whole view fail.
    return {
      text: ngFormatNumber(num, FALLBACK_LOCALE, digitsInfo),
      separators: { group: ',', decimal: '.' },
    };
  }
}

/**
 * Rewrites `text`'s separators from CLDR's to MIQ's, preserving CLDR's GROUPING POSITIONS.
 *
 * Positions are kept on purpose: `en-IN` groups by lakh (`12,34,567.89`) and MIQ has always shown
 * Indian users whatever Angular produced. Regenerating the groups at a fixed 3 digits would silently
 * change that, so only the characters are substituted.
 */
function rewriteSeparators(text: string, cldr: MiqNumberSeparators, target: MiqNumberSeparators): string {
  if (cldr.group === target.group && cldr.decimal === target.decimal) {
    return text;
  }
  const decimalIdx = cldr.decimal ? text.lastIndexOf(cldr.decimal) : -1;
  const intPart = decimalIdx >= 0 ? text.slice(0, decimalIdx) : text;
  const fracPart = decimalIdx >= 0 ? text.slice(decimalIdx + cldr.decimal.length) : null;

  const rewrittenInt = Array.from(intPart)
    .map(ch => (isCldrGroupChar(ch, cldr.group) ? target.group : ch))
    .join('');
  return fracPart == null ? rewrittenInt : rewrittenInt + target.decimal + fracPart;
}

/**
 * Whether `ch` in the integer part is CLDR's grouping separator.
 *
 * The space-family check is not redundant: CLDR's declared group symbol and the character the
 * formatter emits are not always the same codepoint (U+00A0 vs U+202F vs U+0020 across locales and
 * Angular versions), and treating them as distinct is how the recovered implementation let
 * de-CH/it-CH slip through unconverted.
 */
function isCldrGroupChar(ch: string, cldrGroup: string): boolean {
  if (ch === cldrGroup) {
    return true;
  }
  if (isSpaceVariant(cldrGroup) && isSpaceVariant(ch)) {
    return true;
  }
  // Last resort: any character CLDR is known to group with, but never a digit or a sign.
  return isSpaceVariant(ch) || (isGroupingChar(ch) && ch !== '.' && ch !== ',');
}
